import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export const CLOUD_RUNTIME_PROJECT_HANDOFF_STORAGE_KEY = "forge:cloud-runtime-handoffs:v2";

export interface CloudRuntimeProjectHandoff {
  readonly runtimeId: string;
  readonly previousCatalogRuntimeId: string | null;
  readonly catalogRegisteredAt: string;
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
  readonly recordedAt: number;
}

interface CloudRuntimeProjectHandoffState {
  readonly byRuntimeId: Readonly<Record<string, CloudRuntimeProjectHandoff>>;
  readonly record: (handoff: CloudRuntimeProjectHandoff) => void;
  readonly clearRuntime: (runtimeId: string) => void;
  readonly reconcileCatalog: (
    rows: readonly CloudRuntimeProjectHandoffCatalogRow[],
    nowMs?: number,
  ) => void;
  readonly reset: () => void;
}

interface CloudRuntimeProjectHandoffCatalogRow {
  readonly runtimeId: string | null;
  readonly registeredAt: string | null;
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
}

const HANDOFF_MAX_AGE_MS = 5 * 60_000;
const fallbackStorageValues = new Map<string, string>();
const fallbackStorage: Storage = {
  clear: () => fallbackStorageValues.clear(),
  getItem: (key) => fallbackStorageValues.get(key) ?? null,
  key: (index) => Array.from(fallbackStorageValues.keys())[index] ?? null,
  get length() {
    return fallbackStorageValues.size;
  },
  removeItem: (key) => fallbackStorageValues.delete(key),
  setItem: (key, value) => fallbackStorageValues.set(key, value),
};

function resolveHandoffStorage(): Storage {
  if (typeof window !== "undefined") return window.localStorage;
  const globalStorage = (globalThis as { readonly localStorage?: Storage }).localStorage;
  return globalStorage ?? fallbackStorage;
}

export const useCloudRuntimeProjectHandoffStore = create<CloudRuntimeProjectHandoffState>()(
  persist(
    (set) => ({
      byRuntimeId: {},
      record: (handoff) =>
        set((state) => ({
          byRuntimeId: { ...state.byRuntimeId, [handoff.runtimeId]: handoff },
        })),
      clearRuntime: (runtimeId) =>
        set((state) => {
          if (!(runtimeId in state.byRuntimeId)) return state;
          const byRuntimeId = Object.fromEntries(
            Object.entries(state.byRuntimeId).filter(([candidate]) => candidate !== runtimeId),
          );
          return { byRuntimeId };
        }),
      reconcileCatalog: (rows, nowMs = Date.now()) =>
        set((state) => {
          const retained = Object.entries(state.byRuntimeId).filter(([, handoff]) => {
            if (nowMs - handoff.recordedAt > HANDOFF_MAX_AGE_MS) return false;
            const row = rows.find(
              (candidate) =>
                candidate.cmmdOrganizationId === handoff.cmmdOrganizationId &&
                candidate.cmmdProjectId === handoff.cmmdProjectId,
            );
            return (
              row?.runtimeId === handoff.previousCatalogRuntimeId &&
              row.registeredAt === handoff.catalogRegisteredAt
            );
          });
          if (retained.length === Object.keys(state.byRuntimeId).length) return state;
          return { byRuntimeId: Object.fromEntries(retained) };
        }),
      reset: () => set({ byRuntimeId: {} }),
    }),
    {
      name: CLOUD_RUNTIME_PROJECT_HANDOFF_STORAGE_KEY,
      storage: createJSONStorage(resolveHandoffStorage),
      partialize: (state) => ({ byRuntimeId: state.byRuntimeId }),
      version: 2,
    },
  ),
);

export function recordCloudRuntimeProjectHandoff(
  input: Omit<CloudRuntimeProjectHandoff, "recordedAt">,
): void {
  useCloudRuntimeProjectHandoffStore.getState().record({ ...input, recordedAt: Date.now() });
}

export function clearCloudRuntimeProjectHandoff(runtimeId: string): void {
  useCloudRuntimeProjectHandoffStore.getState().clearRuntime(runtimeId);
}
